<template>
    <div>
        <form :action="formAction" method="POST" class="form-grid form-grid--xs-1-column" @submit.prevent="submitForm" ref="form">
            <div class="form-grid__col-span-2 form-grid__col--xs-full-width" v-if="submitted" ref="thank-you-message">
                <h2>Thank you for your enquiry</h2>
                <p>
                    A member of the team will be in touch shortly to discuss your enquiry. If you would like to continue your search or get a free instant valuation please use the links below.
                </p>
                <div class="btn-container btn-container--centred btn-container--left-xs btn-container--vertical-xs">
                    <a :href="showroomLink" class="btn btn--primary">Discover more</a>
                    <button type="button" @click="scrollToValuation" class="btn btn--primary-inverted">Value my car</button>
                </div>
            </div>
            <template v-else>
                <div class="form-grid__col-span-2 form-grid__col--xs-full-width">
                    <h2 v-if="isMotability">Discuss your Motability enquiry with one of our {{ vehicle.manufacturer }} Motability Specialists. </h2>
                    <p v-if="isMotability">Fill in the fields below and a member of the team will call you back shortly.</p>
                    <h2 v-else>Complete the form below to enquire about this {{ vehicle.new_model.model_title }}</h2>
                </div>
                <div class="form-grid__col-span-2 form-grid__col--xs-full-width">
                    <NewTitle />
                </div>
                <div class="form-grid__col form-grid__col--xs-full-width">
                    <FirstName />
                </div>
                <div class="form-grid__col form-grid__col--xs-full-width">
                    <LastName />
                </div>
                <div class="form-grid__col form-grid__col--xs-full-width">
                    <Email />
                </div>
                <div class="form-grid__col form-grid__col--xs-full-width">
                    <Telephone :inputNullable="false" inputValidateMessage="Please enter a telephone number" />
                </div>
                <div class="form-grid__col form-grid__col--xs-full-width">
                    <customer-location />
                </div>
                <div class="form-grid__col form-grid__col--xs-full-width">
                    <dealer :filter-new="true" />
                </div>
                <div class="form-grid__col-span-2 form-grid__col--xs-full-width">
                    <generic-textarea input-name="message" input-title="Anything you'd like to ask?" :input-validate="false"></generic-textarea>
                </div>
                <div class="form-grid__col-span-2 form-grid__col--xs-full-width">
                    <privacy></privacy>
                </div>
                <div class="form-grid__col-span-2 form-grid__col--xs-full-width">
                    <button type="submit" class="btn btn--primary btn--min-width">Enquire Now</button>
                </div>
            </template>
        </form>
    </div>
</template>

<script>
import NewTitle from "../../../core/components/Forms/Title.vue";
import FirstName from "../../../core/components/Forms/FirstName.vue";
import LastName from "../../../core/components/Forms/LastName.vue";
import Email from "../../../core/components/Forms/Email.vue";
import Telephone from "../../../core/components/Forms/Telephone.vue";
import CustomerLocation from "../../../core/components/Forms/CustomerLocation.vue";
import Dealer from "../../../core/components/Forms/Dealer.vue";
import Privacy from "../../../core/components/Forms/Privacy.vue";
import GenericTextarea from "../../../core/components/Forms/GenericTextarea.vue";
import ContactFormValidator from "../../../validation/ContactFormValidator.js";
import CustomerLocationHandler from "../../../modules/CustomerLocationHandler.js";
import { mapState } from "vuex";
import axios from "axios";
import { customEvent, generateLeadEvent } from "../../../modules/tracking/AnalyticsEvents";

export default {
    components: {
        NewTitle,
        FirstName,
        LastName,
        Email,
        Telephone,
        CustomerLocation,
        Dealer,
        Privacy,
        GenericTextarea
    },
    props: {
        shouldAttachFinance: {
            type: Boolean,
            required: false,
            default: false,
        },
        shouldAttachPch: {
            type: Boolean,
            required: false,
            default: false,
        },
        isMotability: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    computed: {
        ...mapState('NewVehicleData', ['vehicle']),
        ...mapState('Finance', ['quote', 'quoteWithPredictableKeys']),
        showroomLink() {
            if(this.isMotability){
                return `/${this.vehicle.brand}/motability/${this.vehicle.new_model.url_model}`;
            }else{
                return `/${this.vehicle.brand}/new/${this.vehicle.new_model.url_model}`;
            }
        },
        formAction() {
            return this.isMotability ? `/${this.vehicle.brand}/motability/request-a-callback` : '/api/new/enquire-now';
        },
    },
    data() {
        return {
            validator: null,
            customerLocationHandler: null,
            submitted: false,
        };
    },
    mounted() {
        this.validator = new ContactFormValidator({
            vueForm: true,
            redesign: true,
            element: this.$refs["form"],
        });
        this.customerLocationHandler = new CustomerLocationHandler({
            element: this.$refs["form"],
            additionalLocationFilterFunction: dealer => (dealer.brand == this.vehicle.brand && dealer.codeweavers_key != null && dealer.codeweavers_key.length > 0)
        });
    },
    methods: {
        scrollToValuation() {
            this.$emit('scroll-to-valuation');
        },
        async submitForm() {
            if (!await this.validator.isValid()) {
                return;
            }

            reCaptchaProtect(() => {
                const formData = new FormData(this.$refs.form);
                formData.append('vehicle-id', this.vehicle.id);
                if (this.shouldAttachFinance) {
                    try {
                        formData.append('quote', JSON.stringify(this.quote));
                        formData.append('finance', JSON.stringify(this.quoteWithPredictableKeys));
                    } catch (error) {
                        console.error(error);
                    }
                } else if (this.shouldAttachPch) {
                    formData.append('pch', 1);
                }

                this.validator.preventInput();

                axios.post(this.$refs.form.getAttribute('action'), formData)
                    .then(response => {
                        this.validator.successResponse();
                        this.submitted = true;
                        if(this.isMotability){
                            customEvent({
                                category: "Motability - Request a Callback",
                                label: "Form",
                                action: "Complete",
                            });
                            generateLeadEvent('motability_request_callback');
                        }else{
                            customEvent({
                                category: "New Vehicle - Enquiry",
                                label: "Form",
                                action: "Complete",
                            });
                            generateLeadEvent('new_finance_enquiry');
                        }
                        this.$nextTick(function() {
                            this.$refs['thank-you-message'].scrollIntoView({ behavior: 'smooth', block: 'center' });
                        });
                    })
                    .catch(error => {
                        this.validator.errorResponse(error);
                    })
            });
        }
    }
};
</script>

<style></style>